/**
 * Protected Accounts page — "Funil de Escopo" (LDS).
 *
 * Displays a two-level scope view:
 * 1. Organizational scope (Account Discovery universe)
 * 2. Guardrail scope (effective subset with paginated account list)
 *
 * Supports 200+ accounts via server-side pagination.
 *
 * Requirements: 6.2, 6.3, 6.4, 6.5
 */

import { useState, useEffect, useCallback } from 'react';
import {
  LdsBox,
  LdsPaper,
  LdsTypography,
  LdsFormField,
  LdsTextField,
  LdsTable,
  LdsAlert,
  LdsProgressCircular,
  LdsChip,
  LdsButton,
} from '@lds/react';
import { LdsIconBuildingBank, LdsIconShieldCheck } from '@lds/react-icons';
import { AxiosInstance } from 'axios';
import styles from './AccountsPage.module.scss';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ScopeKind = 'wildcard' | 'accounts' | 'ous' | 'roots' | 'mixed' | 'empty';
export type ScopeMatch = 'full' | 'subset' | 'empty';

export interface ProtectedAccountsView {
  guardrail: string;
  scopeKind: ScopeKind;
  rawScope: string[];
  accounts: Array<{ accountId: string; accountName?: string }>;
  coverageNote: string;
  totalAccounts: number;
  page: number;
  pageSize: number;
  hasNext: boolean;
}

export interface OrganizationalScopeView {
  mode: string;
  rootScope: string[];
  totalDiscoveredAccounts: number;
  mgmtAccountId: string;
}

export interface AccountsScopeResponse {
  organizational: OrganizationalScopeView;
  guardrail: ProtectedAccountsView;
  scopeMatch: ScopeMatch;
}

export interface AccountsPageProps {
  apiClient: AxiosInstance;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const SCOPE_KIND_LABELS: Record<ScopeKind, string> = {
  wildcard: 'Wildcard',
  accounts: 'Contas',
  ous: 'OUs',
  roots: 'Roots',
  mixed: 'Misto',
  empty: 'Vazio',
};

const SCOPE_MATCH_LABELS: Record<ScopeMatch, string> = {
  full: 'Escopo completo',
  subset: 'Subconjunto',
  empty: 'Sem cobertura',
};

function getScopeMatchClass(match: ScopeMatch): string {
  switch (match) {
    case 'full':
      return styles.scopeMatchFull;
    case 'subset':
      return styles.scopeMatchSubset;
    case 'empty':
      return styles.scopeMatchEmpty;
    default:
      return '';
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function AccountsPage({ apiClient }: AccountsPageProps) {
  const [data, setData] = useState<AccountsScopeResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 50;

  const fetchScope = useCallback(
    async (targetPage: number) => {
      setLoading(true);
      setError(null);
      try {
        const response = await apiClient.get<AccountsScopeResponse>(
          '/accounts/scope',
          { params: { page: targetPage, page_size: pageSize } }
        );
        if (!response.data?.organizational) {
          throw new Error('Falha ao autenticar. Verifique suas permissões de acesso.');
        }
        setData(response.data);
        setPage(targetPage);
      } catch (err: unknown) {
        const message =
          err instanceof Error ? err.message : 'Falha ao carregar contas';
        setError(message);
      } finally {
        setLoading(false);
      }
    },
    [apiClient]
  );

  useEffect(() => {
    fetchScope(1);
  }, [fetchScope]);

  // Client-side filter on the current page (for quick search within loaded data)
  const filteredAccounts = data?.guardrail?.accounts?.filter((account) => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      account.accountId.toLowerCase().includes(term) ||
      (account.accountName?.toLowerCase().includes(term) ?? false)
    );
  });

  if (loading && !data) {
    return (
      <LdsBox className={styles.centered} data-testid="accounts-loading">
        <LdsProgressCircular indeterminate animated ariaLabel="Carregando" />
      </LdsBox>
    );
  }

  if (error) {
    return (
      <LdsAlert color="critical" description={error} data-testid="accounts-error" />
    );
  }

  if (!data) return null;

  const { organizational, guardrail, scopeMatch } = data;
  const totalAccounts = guardrail?.totalAccounts ?? 0;
  const totalPages = Math.ceil(totalAccounts / pageSize);
  const startItem = (page - 1) * pageSize + 1;
  const endItem = Math.min(page * pageSize, totalAccounts);

  return (
    <LdsBox data-testid="accounts-page">
      <LdsTypography variant="headingXl">Contas Protegidas</LdsTypography>

      {/* Funil de Escopo */}
      <div className={styles.scopeFunnel}>
        {/* Card 1: Escopo Organizacional */}
        <LdsPaper elevated className={styles.orgScopeCard}>
          <div className={styles.orgScopeHeader}>
            <span className={styles.orgScopeIcon}>
              <LdsIconBuildingBank size="24" />
            </span>
            <LdsTypography variant="headingMd">
              Escopo Organizacional
            </LdsTypography>
            <LdsChip
              label={`${organizational.totalDiscoveredAccounts} contas`}
              size="sm"
              color="neutral"
            />
          </div>

          <div className={styles.orgScopeDetails}>
            <div className={styles.orgScopeDetail}>
              <LdsTypography variant="bodySm" color="secondary">
                Modo
              </LdsTypography>
              <LdsTypography variant="bodyMd">
                {organizational.mode === 'org_api'
                  ? 'Organizations API'
                  : organizational.mode}
              </LdsTypography>
            </div>
            <div className={styles.orgScopeDetail}>
              <LdsTypography variant="bodySm" color="secondary">
                Scope raiz
              </LdsTypography>
              <LdsTypography variant="bodyMd">
                {organizational.rootScope.join(', ') || '—'}
              </LdsTypography>
            </div>
            <div className={styles.orgScopeDetail}>
              <LdsTypography variant="bodySm" color="secondary">
                Conta MGMT
              </LdsTypography>
              <LdsTypography variant="bodyMd">
                {organizational.mgmtAccountId || '—'}
              </LdsTypography>
            </div>
          </div>

          <LdsTypography
            variant="bodySm"
            className={styles.orgScopeNote}
          >
            Universo de contas que o sistema SGR consegue alcançar.
          </LdsTypography>
        </LdsPaper>

        {/* Conector */}
        <div className={styles.funnelConnector}>
          <div className={styles.funnelArrow}>
            <span>▼</span>
            <LdsTypography variant="bodySm">filtrado por guardrail</LdsTypography>
          </div>
        </div>

        {/* Card 2: Escopo da Guardrail */}
        <LdsPaper elevated className={styles.guardrailScopeCard}>
          <div className={styles.guardrailHeader}>
            <div className={styles.guardrailHeaderLeft}>
              <LdsIconShieldCheck size="24" />
              <LdsTypography variant="headingMd">
                Guardrail: {guardrail?.guardrail ?? 'iam-accesskeys'}
              </LdsTypography>
            </div>
            <LdsChip
              label={SCOPE_MATCH_LABELS[scopeMatch]}
              size="sm"
              className={getScopeMatchClass(scopeMatch)}
            />
          </div>

          {/* Coverage note */}
          <div className={styles.coverage} data-testid="coverage-note">
            <LdsChip
              label={SCOPE_KIND_LABELS[guardrail?.scopeKind ?? 'empty']}
              size="sm"
              color="info"
              data-testid="scope-kind"
            />
            <LdsTypography variant="bodyMd">
              {guardrail?.coverageNote ?? ''}
            </LdsTypography>
            <LdsChip
              label={`${totalAccounts} contas`}
              size="sm"
              color="neutral"
            />
          </div>

          {/* Raw scope (colapsável para escopo OUs) */}
          {(guardrail?.rawScope?.length ?? 0) > 0 &&
            guardrail?.scopeKind !== 'accounts' && (
              <div className={styles.rawScopeBlock}>
                <LdsTypography variant="bodySm">
                  Scope configurado:{' '}
                  <code>{JSON.stringify(guardrail?.rawScope ?? [])}</code>
                </LdsTypography>
              </div>
            )}
        </LdsPaper>
      </div>

      {/* Search */}
      {totalAccounts > 0 && (
        <LdsPaper elevated className={styles.searchCard}>
          <LdsBox className={styles.searchField}>
            <LdsFormField
              label="Buscar por ID ou nome da conta"
              fieldId="accounts-search"
            >
              <LdsTextField
                value={searchTerm}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setSearchTerm(e.target.value)
                }
                aria-label="Buscar contas"
                data-field="accounts-search"
              />
            </LdsFormField>
          </LdsBox>
        </LdsPaper>
      )}

      {/* Accounts Table */}
      {filteredAccounts && filteredAccounts.length > 0 ? (
        <LdsPaper elevated>
          <LdsTable
            aria-label="Tabela de contas protegidas"
            data-testid="accounts-table"
            striped
          >
            <thead>
              <tr>
                <th>Account ID</th>
                <th>Account Name</th>
              </tr>
            </thead>
            <tbody>
              {filteredAccounts.map((account) => (
                <tr key={account.accountId}>
                  <td data-testid={`account-id-${account.accountId}`}>
                    {account.accountId}
                  </td>
                  <td>{account.accountName || '—'}</td>
                </tr>
              ))}
            </tbody>
          </LdsTable>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className={styles.pagination}>
              <span className={styles.paginationInfo}>
                <LdsTypography variant="bodySm">
                  {startItem}–{endItem} de {totalAccounts} contas
                </LdsTypography>
              </span>
              <div className={styles.paginationButtons}>
                <LdsButton
                  size="sm"
                  variant="outlined"
                  disabled={page <= 1 || loading}
                  onClick={() => fetchScope(page - 1)}
                  aria-label="Página anterior"
                >
                  Anterior
                </LdsButton>
                <LdsButton
                  size="sm"
                  variant="outlined"
                  disabled={!guardrail?.hasNext || loading}
                  onClick={() => fetchScope(page + 1)}
                  aria-label="Próxima página"
                >
                  Próxima
                </LdsButton>
              </div>
            </div>
          )}
        </LdsPaper>
      ) : totalAccounts === 0 ? (
        <LdsAlert
          color="info"
          description={
            guardrail?.scopeKind === 'empty'
              ? 'Guardrail em fase inerte — nenhuma conta coberta.'
              : 'Nenhuma conta encontrada para o escopo configurado.'
          }
          data-testid="accounts-empty"
        />
      ) : (
        <LdsAlert
          color="info"
          description="Nenhuma conta corresponde aos critérios de busca na página atual."
          data-testid="accounts-empty"
        />
      )}

      {/* Loading overlay for pagination */}
      {loading && data && (
        <LdsBox className={styles.centered}>
          <LdsProgressCircular
            indeterminate
            animated
            ariaLabel="Carregando página"
          />
        </LdsBox>
      )}
    </LdsBox>
  );
}
